import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PHONE_TEL } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl border border-white/10 bg-obsidian/60 px-3.5 py-3 text-sm font-semibold text-white placeholder:text-slate-600 outline-none transition-all focus:border-volt/60 focus:ring-2 focus:ring-volt/25";
const labelCls =
    "mb-1.5 block text-[11px] font-extrabold uppercase tracking-[0.15em] text-slate-400";

const initial = {
    name: "", phone: "", zip_code: "", year: "", make: "", model: "",
    condition: "", title_status: "Clean title",
};

export const QuoteForm = () => {
    const { t } = useLang();
    const [form, setForm] = useState(initial);
    const [issues, setIssues] = useState([]);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggleIssue = (label) =>
        setIssues((arr) => (arr.includes(label) ? arr.filter((x) => x !== label) : [...arr, label]));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await fetch(`${API}/quotes`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, issues }),
            });
            if (!res.ok) throw new Error(String(res.status));
            const data = await res.json();
            setDone(form.name);
            toast.success(t("success.t"), { description: t("quote.privacy") });
        } catch (err) {
            toast.error(t("quote.errSubmit"));
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="quote-success-panel"
                className="flex h-full flex-col items-center justify-center rounded-3xl border border-volt/30 bg-elevated p-8 sm:p-12 text-center"
            >
                <span className="grid h-16 w-16 place-items-center rounded-full bg-volt/15 border border-volt/40">
                    <CheckCircle2 className="h-8 w-8 text-volt" />
                </span>
                <h3 className="mt-5 font-display text-2xl sm:text-3xl font-black text-white">{t("success.t")}</h3>
                <p className="mt-3 max-w-sm text-sm sm:text-base leading-relaxed text-slate-400">
                    {t("success.b").replace("{name}", done)}
                </p>
                <a
                    href={PHONE_TEL}
                    data-testid="success-call-btn"
                    className="mt-7 inline-flex items-center gap-2.5 rounded-2xl bg-volt px-6 py-3.5 font-extrabold text-obsidian transition-all hover:bg-volt-dark active:scale-[0.98]"
                >
                    <Phone className="h-4 w-4" />
                    {t("success.call")}
                </a>
                <button
                    type="button"
                    data-testid="quote-again-btn"
                    onClick={() => {
                        setForm(initial);
                        setIssues([]);
                        setDone(null);
                    }}
                    className="mt-4 text-sm font-bold text-slate-400 underline underline-offset-4 transition-colors hover:text-volt"
                >
                    {t("success.again")}
                </button>
            </motion.div>
        );
    }

    const titleOptions = [
        { value: t("quote.titles")[0], testid: "title-clean-btn", accent: "border-volt/60 bg-volt/15 text-volt" },
        { value: t("quote.titles")[1], testid: "title-salvage-btn", accent: "border-volt/60 bg-volt/15 text-volt" },
        { value: t("quote.titles")[2], testid: "title-none-btn", accent: "border-blaze/70 bg-blaze/15 text-blaze" },
    ];

    return (
        <form onSubmit={onSubmit} data-testid="instant-quote-form" className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className={labelCls} htmlFor="q-year">{t("quote.year")}</label>
                    <select id="q-year" required data-testid="year-select" value={form.year} onChange={set("year")} className={`${inputCls} appearance-none`}>
                        <option value="" disabled>{t("quote.select")}</option>
                        {Array.from({ length: 2026 - 1990 + 1 }, (_, i) => 2026 - i).map((y) => (
                            <option key={y} value={y} className="bg-surface">{y}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="q-make">{t("quote.make")}</label>
                    <select id="q-make" required data-testid="make-select" value={form.make} onChange={set("make")} className={`${inputCls} appearance-none`}>
                        <option value="" disabled>{t("quote.select")}</option>
                        {["Ford", "Chevrolet", "Toyota", "Nissan", "Honda", "RAM", "GMC", "Dodge", "Kia", "Hyundai", "BMW", "Mercedes-Benz", "Jeep", "Other"].map((m) => (
                            <option key={m} value={m} className="bg-surface">{m}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="q-model">{t("quote.model")}</label>
                    <input id="q-model" type="text" required data-testid="model-input" value={form.model} onChange={set("model")} placeholder={t("quote.modelPh")} className={inputCls} />
                </div>
            </div>

            <div>
                <label className={labelCls} htmlFor="q-condition">{t("quote.condition")}</label>
                <select id="q-condition" required data-testid="condition-select" value={form.condition} onChange={set("condition")} className={`${inputCls} appearance-none`}>
                    <option value="" disabled>{t("quote.select")}</option>
                    {t("quote.cond").map((c) => (
                        <option key={c} value={c} className="bg-surface">{c}</option>
                    ))}
                </select>
            </div>

            <div>
                <span className={labelCls}>{t("quote.title")}</span>
                <div className="grid grid-cols-3 gap-2" data-testid="title-status-group">
                    {titleOptions.map((o) => (
                        <button
                            type="button"
                            key={o.testid}
                            data-testid={o.testid}
                            aria-pressed={form.title_status === o.value}
                            onClick={() => setForm((f) => ({ ...f, title_status: o.value }))}
                            className={`rounded-xl border p-2.5 text-xs sm:text-sm font-extrabold transition-all active:scale-[0.97] ${
                                form.title_status === o.value
                                    ? o.accent
                                    : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25"
                            }`}
                        >
                            {o.value}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <span className={labelCls}>{t("quote.issues")}</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {t("quote.iss").map((label, i) => {
                        const on = issues.includes(label);
                        return (
                            <button
                                type="button"
                                key={label}
                                data-testid={`issue-${i}-checkbox`}
                                aria-pressed={on}
                                onClick={() => toggleIssue(label)}
                                className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-[11px] sm:text-xs font-bold transition-all active:scale-[0.97] ${
                                    on
                                        ? "border-volt/60 bg-volt/15 text-volt"
                                        : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25"
                                }`}
                            >
                                <span className={`grid h-3.5 w-3.5 place-items-center rounded-sm border ${on ? "border-volt bg-volt" : "border-slate-600"}`}>
                                    {on && <CheckCircle2 className="h-3 w-3 text-obsidian" />}
                                </span>
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                <div>
                    <label className={labelCls} htmlFor="q-name">{t("quote.name")}</label>
                    <input id="q-name" type="text" required data-testid="name-input" value={form.name} onChange={set("name")} className={inputCls} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="q-phone">{t("quote.phone")}</label>
                    <input id="q-phone" type="tel" required data-testid="phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="q-zip">{t("quote.zip")}</label>
                    <input id="q-zip" type="text" data-testid="zip-input" value={form.zip_code} onChange={set("zip_code")} className={inputCls} />
                </div>
            </div>

            <button
                type="submit"
                disabled={sending}
                data-testid="submit-quote-btn"
                className="relative w-full rounded-2xl bg-volt py-4 font-display text-base sm:text-lg font-black uppercase tracking-wide text-obsidian transition-all hover:bg-volt-dark active:scale-[0.98] disabled:opacity-60 shadow-[0_0_40px_rgba(212,255,0,0.25)]"
            >
                {sending ? t("quote.submitting") : t("quote.submit")}
            </button>
            <p className="text-center text-xs font-semibold text-slate-500">{t("quote.privacy")}</p>
        </form>
    );
};

export default QuoteForm;
